import { useEffect, useId } from "react";

// A small reusable confirmation dialog, styled to match the app's other modals (dark card,
// bordered header/footer, the shared overlay/dialog entrance animation). Replaces native
// window.confirm() for destructive actions. The parent owns the open state — render it only when a
// confirmation is pending.
export default function ConfirmModal({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();

  // Escape cancels (unless an action is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/70 backdrop-blur-sm animate-overlay-in"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex min-h-full items-start justify-center p-6 pt-24">
        <div
          className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] shadow-2xl animate-dialog-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2.5 border-b border-white/10 px-6 py-4">
            <span className={`shrink-0 ${destructive ? "text-red-400" : "text-primary"}`} aria-hidden>
              {destructive ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
              )}
            </span>
            <h2 id={titleId} className="font-display text-base font-semibold text-text">{title}</h2>
          </div>
          <div className="px-6 py-5 text-sm leading-relaxed text-text/70">{message}</div>
          <div className="flex items-center justify-end gap-2 border-t border-white/10 px-6 py-4">
            <button
              onClick={onCancel}
              disabled={busy}
              className="rounded-lg border border-white/15 px-4 py-2 text-sm text-text/70 transition hover:bg-white/5 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition disabled:opacity-60 ${
                destructive ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:opacity-90"
              }`}
            >
              {busy ? "Working…" : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
